import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "using-triggers-and-rules"
    }}>{`Using Triggers and Rules`}</h1>
    <p>{`Invoking an action directly in the CLI is quite limited; what you want to do is configure Adobe I/O Runtime to respond to events. The Runtime features that enable you to do this are triggers and rules:`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`Triggers`}</em>{` are events: each trigger you create represents a particular class of event.`}</li>
      <li parentName="ul"><em parentName="li">{`Rules`}</em>{` are associations between triggers and actions. Rules are the way you make Runtime execute code in response to an event.`}</li>
    </ul>
    <h2 {...{
      "id": "about-triggers"
    }}>{`About triggers`}</h2>
    <p>{`Triggers are a way for you to apply a name to a class of events. Some examples:`}</p>
    <ul>
      <li parentName="ul">{`updating a user`}{`’`}{`s location on a mobile app`}</li>
      <li parentName="ul">{`uploading a file to cloud storage`}</li>
      <li parentName="ul">{`sending an email or text message`}</li>
    </ul>
    <p>{`Almost anything can be classified as an event by defining it as a trigger. In Runtime, events are defined by means of a set of key-value pairs; this allows them to be passed to an action as a JSON object. Triggers are used to define the keys for these pairs. When a trigger is activated (`}<em parentName="p">{`fired`}</em>{`), it reads from the event source the values for these keys and passes on a dictionary of key-value pairs that define the event.`}</p>
    <p>{`You can fire a trigger explicitly in the CLI yourself, but it`}{`’`}{`s much more common for an event source to be configured to fire triggers via a feed. For more information, see `}<a parentName="p" {...{
        "href": "feeds.md",
        "title": "Using feeds"
      }}>{`Using Feeds`}</a>{`.`}</p>
    <h2 {...{
      "id": "about-rules"
    }}>{`About rules`}</h2>
    <p>{`Rules link triggers with actions. Each rule links a specific trigger with a single action: every time the trigger is fired, the rule causes the action to be invoked with the trigger`}{`’`}{`s event used as input parameters. By combining rules, however, you can cause a trigger to fire multiple actions, or cause a action to respond to multiple triggers. `}</p>
    <p>{`For example, consider a mobile app that tracks the user`}{`’`}{`s location when the user takes a photo, records the user`}{`’`}{`s GPS coordinates, and uploads the photo to the user`}{`’`}{`s Adobe Assetsa account, in a library named `}{`“`}{`My Photos`}{`”`}{`. The app also captures images sent or received via text or email and uploads them to the user`}{`’`}{`s Adobe Assets, in another library named `}{`“`}{`Shared with Me`}{`”`}{`. You could configure the following actions:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`catalogImg`}</inlineCode>{`: calls a Sensei Studio function to analyze the image and recognize objects`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mapImg`}</inlineCode>{`: adds metadata to the image to log its GPS coordinates`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`thumbImg`}</inlineCode>{`: creates a thumbnail of an image`}</li>
    </ul>
    <p>{`Using the mobile app as an event source, you could set the following triggers:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`myPhoto`}</inlineCode>{`: fires when the app uploads a photo taken by the user`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`sharedImg`}</inlineCode>{`: fires when the app uploads an image sent or received via email or text message`}</li>
    </ul>
    <p>{`To process these images in Runtime, you could set the following rules:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`myPhoto -> catalogImg`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`myPhoto -> mapImg`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`myPhoto -> thumbImg`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`sharedImg -> catalogImg`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`sharedImg -> thumbImg`}</inlineCode></li>
    </ul>
    <p>{`So, with these rules, both images taken by the user and images sent or received via email or text are catalogued and thumbnails are created; for the user`}{`’`}{`s own images only, GPS coordinates are tracked.`}</p>
    <h2 {...{
      "id": "creating-and-firing-triggers"
    }}>{`Creating and firing triggers`}</h2>
    <p>{`Creating triggers is easy. Consider a trigger to update a user`}{`’`}{`s location; every time the trigger is fired, it sends the user`}{`’`}{`s name and GPS coordinates. To create the trigger in the CLI:`}</p>
    <p><inlineCode parentName="p">{`aio rt:trigger:create updateLoc`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`ok: created trigger updateLoc
`}</code></pre>
    <p>{`And, just as with packages and actions, you can list the triggers you`}{`’`}{`ve created:`}</p>
    <p><inlineCode parentName="p">{`aio  rt:trigger:list`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`triggers
/yourNamespace/updateLoc                                              private
`}</code></pre>
    <p>{`That was simple; but then, all you`}{`’`}{`ve really done here is create a label for a channel through which events can be passed to the system. You can fire this trigger just as simply, specifying the parameters:`}</p>
    <p><inlineCode parentName="p">{`aio rt:trigger:fire updateLoc --param name Jocasta latitude 38.316667 longitude 23.316667`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`ok: triggered updateLoc with id 94dddb5ef8ca46e9ba4648eeb9bda80f
`}</code></pre>
    <p>{`So, the trigger has been fired`}{`—`}{`but nothing happened. You haven`}{`’`}{`t set up a rule yet to link this trigger with an action. Triggers that fire with no associated rule have no effect.`}</p>
    <h2 {...{
      "id": "creating-and-using-rules"
    }}>{`Creating and using rules`}</h2>
    <p>{`To link your trigger with an action, you need to create a rule. To do so, you need the name of your trigger, the name of the action you`}{`’`}{`re going to execute when the trigger is fired, and a name for your rule itself. `}</p>
    <p>{`To give your trigger an appropriate action, create an action named `}<inlineCode parentName="p">{`helloLoc`}</inlineCode>{` using the following code. (See `}<a parentName="p" {...{
        "href": "../gettingstarted/deploy.md",
        "title": "Deploying your first function"
      }}>{`Deploying your first Adobe I/O Runtime function`}</a>{` for instructions on creating an action):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`function main(params) {
   return {payload:  'Hello, ' + params.name + ', I see you are at latitude ' + params.latitude ', longitude ' + params.longitude };
}
`}</code></pre>
    <p>{`Now you can create the rule linking the trigger to the action:`}</p>
    <p><inlineCode parentName="p">{`aio rt:rule:create greetLoc updateLoc helloLoc`}</inlineCode></p>
    <p>{`This creates a rule named `}<inlineCode parentName="p">{`greetLoc`}</inlineCode>{` that executes `}<inlineCode parentName="p">{`helloLoc`}</inlineCode>{` every time the trigger `}<inlineCode parentName="p">{`updateLoc`}</inlineCode>{` is fired. Now, fire the trigger again:`}</p>
    <p><inlineCode parentName="p">{`aio rt:trigger:fire updateLoc --param name Jocasta latitude 38.316667 longitude 23.316667`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`ok: triggered updateLoc with id 00e0dd4cce3f43768dabb99d67731b50
`}</code></pre>
    <p>{`To see what happened as a result, you can check your activations. You can list them easily:`}</p>
    <p><inlineCode parentName="p">{`aio rt:activation:list`}</inlineCode></p>
    <p>{`Your `}<inlineCode parentName="p">{`hello`}</inlineCode>{` action should be first on the list. If the list is long, though, you may want to just show the most recent activation:`}</p>
    <p><inlineCode parentName="p">{`aio rt:activation:list --limit 1 helloLoc`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`activations
a48069e1da3c4aa9bc48ac979c5ee140             helloLoc
`}</code></pre>
    <p>{`To see the results of this activation:`}</p>
    <p><inlineCode parentName="p">{`aio rt:activation:result a48069e1da3c4aa9bc48ac979c5ee140`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`{
    payload: "Hello, Jocasta, I see you are at latitude 38.316667, longitude 23.316667"
}
`}</code></pre>
    <p>{`So, the helloLoc action received the parameters from updateLoc and returned the result you`}{`’`}{`d expect.`}</p>
    <p>{`Finally, to stop a rule, you can disable it:`}</p>
    <p><inlineCode parentName="p">{`aio rt:rule:disable greetLoc`}</inlineCode></p>
    <h2 {...{
      "id": "rules-on-triggers-and-rules"
    }}>{`Rules on triggers and rules`}</h2>
    <p>{`Triggers, rules and actions can be combined in different ways: you can associate a trigger with multiple actions by using a different rule for each action, and an action can be the target of multiple triggers, again by creating a rule for each trigger. You cannot, however, link a trigger to more than one action with a single rule, nor can you link more than one trigger to an action with a single rule.`}</p>
    <p>{`Triggers and rules stand outside packages; you cannot place either in a package. You can, however, create a rule that addresses an action in a package. Suppose the `}<inlineCode parentName="p">{`helloLoc`}</inlineCode>{` action is in a package named `}<inlineCode parentName="p">{`manageLoc`}</inlineCode>{`:`}</p>
    <p><inlineCode parentName="p">{`aio rt:rule:create greetLoc updateLoc /manageLoc/helloLoc`}</inlineCode></p>
    <p>{`\``}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      